import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

// Logique pure extraite en dehors du composant
const getValidNeighbors = (state) => {
  const neighbors = [];
  const n = state.length;
  for (let i = 0; i < n - 2; i++) {
    const j = i + 2;
    const nextState = [...state];
    [nextState[i], nextState[j]] = [nextState[j], nextState[i]];
    neighbors.push({ nextState, label: `Saut Pos #${i + 1} ↔ Pos #${j + 1}` });
  }
  if (n >= 2) {
    const nextState = [...state];
    [nextState[0], nextState[1]] = [nextState[1], nextState[0]];
    neighbors.push({ nextState, label: 'Extrémité gauche (#1 ↔ #2)' });
  }
  if (n >= 2) {
    const nextState = [...state];
    [nextState[n - 2], nextState[n - 1]] = [nextState[n - 1], nextState[n - 2]];
    neighbors.push({ nextState, label: `Extrémité droite (#${n - 1} ↔ #${n})` });
  }
  return neighbors;
};

const solvePuzzleAStar = (initialState) => {
  const target = [...initialState].sort((a, b) => a - b);
  const targetKey = target.join(',');
  const startKey = initialState.join(',');

  if (startKey === targetKey) return [];

  const heuristic = (state) => {
    let inv = 0;
    for (let i = 0; i < state.length; i++) {
      for (let j = i + 1; j < state.length; j++) {
        if (state[i] > state[j]) inv++;
      }
    }
    return inv;
  };

  const queue = [{ state: initialState, path: [], g: 0, f: heuristic(initialState) }];
  const visited = new Map();
  visited.set(startKey, 0);

  while (queue.length > 0) {
    queue.sort((a, b) => a.f - b.f);
    const current = queue.shift();

    if (current.state.join(',') === targetKey) return current.path;

    for (const { nextState, label } of getValidNeighbors(current.state)) {
      const key = nextState.join(',');
      const newG = current.g + 1;
      if (!visited.has(key) || newG < visited.get(key)) {
        visited.set(key, newG);
        queue.push({
          state: nextState,
          path: [...current.path, { state: nextState, label }],
          g: newG,
          f: newG + heuristic(nextState)
        });
      }
    }
  }
  return null;
};

function JunzuGame() {
  const [gridSize, setGridSize] = useState(5);
  const [grid, setGrid] = useState([]);
  const [moveCount, setMoveCount] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isSolved, setIsSolved] = useState(false);
  const [swappingIndices, setSwappingIndices] = useState([]);
  const [message, setMessage] = useState({ text: '', isVictory: false });
  
  const stepByStepSolution = useRef([]);
  const currentStepIndex = useRef(-1);

  const generateNewGrid = useCallback((size) => {
    let arr = Array.from({ length: size }, (_, i) => (i + 1) * 10 + Math.floor(Math.random() * 8));
    let current = [...arr].sort((a, b) => a - b);

    for (let i = 0; i < size * 6; i++) {
      const neighbors = getValidNeighbors(current);
      current = neighbors[Math.floor(Math.random() * neighbors.length)].nextState;
    }

    setGrid(current);
    setMoveCount(0);
    setSeconds(0);
    setIsSolved(false);
    stepByStepSolution.current = [];
    currentStepIndex.current = -1;
    setSwappingIndices([]);
    setMessage({ text: '', isVictory: false });
  }, []);

  useEffect(() => {
    generateNewGrid(5);
  }, [generateNewGrid]);

  useEffect(() => {
    let timerInterval = null;
    if (!isSolved && grid.length > 0) {
      timerInterval = setInterval(() => setSeconds(s => s + 1), 1000);
    }
    return () => clearInterval(timerInterval);
  }, [isSolved, grid]);

  const checkVictory = (currentGrid) => {
    const isAscending = currentGrid.every((val, i) => i === 0 || currentGrid[i - 1] <= val);
    if (isAscending && currentGrid.length > 0) {
      setIsSolved(true);
      setMessage({ text: "🎉 Félicitations ! Les valeurs sont triées dans l'ordre croissant !", isVictory: true });
    }
  };

  const triggerSwapAnimation = (indices) => {
    setSwappingIndices(indices);
    setTimeout(() => setSwappingIndices([]), 450);
  };

  const handleTileClick = (index) => {
    if (isSolved) return;
    const n = grid.length;
    let swapped = [];
    let newGrid = [...grid];

    if (index === 0) {
      [newGrid[0], newGrid[1]] = [newGrid[1], newGrid[0]];
      swapped = [0, 1];
    } else if (index === n - 1) {
      [newGrid[n - 2], newGrid[n - 1]] = [newGrid[n - 1], newGrid[n - 2]];
      swapped = [n - 2, n - 1];
    } else {
      const left = index - 1;
      const right = index + 1;
      [newGrid[left], newGrid[right]] = [newGrid[right], newGrid[left]];
      swapped = [left, right];
    }

    setGrid(newGrid);
    setMoveCount(m => m + 1);
    triggerSwapAnimation(swapped);
    checkVictory(newGrid);
  };

  const applyStep = (step) => {
    const swapped = [];
    step.state.forEach((val, idx) => {
      if (val !== grid[idx]) swapped.push(idx);
    });
    setGrid(step.state);
    setMoveCount(m => m + 1);
    triggerSwapAnimation(swapped);
    setMessage({ text: `💡 Pas à pas : ${step.label}`, isVictory: false });
    checkVictory(step.state);
  };

  const handleSolve = () => {
    if (isSolved) return;
    const solution = solvePuzzleAStar(grid);
    if (!solution) return;
    const sorted = [...grid].sort((a, b) => a - b);
    setGrid(sorted);
    setMoveCount(m => m + solution.length);
    checkVictory(sorted);
    setMessage({ text: `⚡ Grille résolue en ${solution.length} coups !`, isVictory: true });
  };

  const handleStepByStep = () => {
    if (isSolved) return;
    if (stepByStepSolution.current.length === 0) {
      const sol = solvePuzzleAStar(grid);
      if (!sol || sol.length === 0) return;
      stepByStepSolution.current = sol;
      currentStepIndex.current = 0;
      applyStep(sol[0]);
    } else {
      const nextIdx = currentStepIndex.current + 1;
      if (nextIdx < stepByStepSolution.current.length) {
        currentStepIndex.current = nextIdx;
        applyStep(stepByStepSolution.current[nextIdx]);
      }
    }
  };

  const formatTime = (secs) => {
    const mins = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${mins}:${s}`;
  };

  return (
    <div className="game-wrapper">
      <div className="game-card">
        <div className="header">
          <h1>🎋 JUNZU</h1>
          <p>Tri Croissant (Petit ➡️ Grand)</p>
        </div>

        <div className="dashboard">
          <div className="stats-box">
            <div className="stat-item">
              <span className="stat-label">⏱️ Temps</span>
              <span className="stat-value">{formatTime(seconds)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">🎯 Coups</span>
              <span className="stat-value">{moveCount}</span>
            </div>
          </div>

          <div className="controls-box">
            <label>Taille ({gridSize}) :</label>
            <input 
              type="range" min="3" max="15" value={gridSize} 
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setGridSize(val);
                generateNewGrid(val);
              }} 
            />
            <button onClick={() => generateNewGrid(gridSize)}>🔄</button>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-flex">
            {grid.map((val, idx) => {
              const isSwapping = swappingIndices.includes(idx);
              const isExtremity = idx === 0 || idx === grid.length - 1;
              const isPrevOK = idx > 0 && grid[idx - 1] <= val;
              const isNextOK = idx < grid.length - 1 && val <= grid[idx + 1];
              const isContiguous = (idx === 0 && isNextOK) || (idx === grid.length - 1 && isPrevOK) || (isPrevOK && isNextOK);

              return (
                <div 
                  key={idx} 
                  onClick={() => handleTileClick(idx)}
                  className={`tile ${isContiguous ? 'tile-correct' : 'tile-normal'} ${isSwapping ? 'bamboo-tile-swapping' : ''}`}
                >
                  <span className="tile-index">#{idx + 1}</span>
                  <span className={`tile-value ${gridSize > 10 ? 'text-xs' : gridSize > 7 ? 'text-sm' : 'text-xl'}`}>{val}</span>
                  <span className="tile-icon">{isExtremity ? '⇄' : '↔'}</span>
                </div>
              );
            })}
          </div>
        </div>

        {message.text && (
          <div className={`message-box ${message.isVictory ? 'message-victory' : 'message-info'}`}>
            {message.text}
          </div>
        )}

        <div className="action-buttons">
          <button onClick={handleStepByStep} disabled={isSolved} className="btn btn-step">🐾 Pas à Pas</button>
          <button onClick={handleSolve} disabled={isSolved} className="btn btn-solve">⚡ Résoudre</button>
          <button onClick={() => generateNewGrid(gridSize)} className="btn btn-new">🎲 Nouveau</button>
        </div>

        <footer>
          <span>Powered by</span>
          <a href="https://patrickm.consulting/mobile-app" target="_blank" rel="noopener noreferrer">PMC Consulting</a>
          <span>© {new Date().getFullYear()}</span> | <Link to="/privacy" className="privacy-link">Confidentialité</Link>
        </footer>
      </div>
    </div>
  );
}

export default JunzuGame;
